import { FC } from 'react';

import { HandMetal, Package, Pencil, Truck } from 'lucide-react';
import { Clock } from 'lucide-react';

import { CancellationDetails } from './CancellationDetails';
import { OrderStatusNotice } from './OrderStatusNotice';
import { StatusNoticeTone } from './orderStatusNoticeTone';
import { OrderTelegramContactNotice } from './OrderTelegramContactNotice';
import {
    formatServiceFeeMoney,
    ServiceFeeBalanceStatus,
} from '../../../components/shared/serviceFeePresentation';
import { Button } from '../../../components/ui/Button';
import { OrdersService } from '../../../services/OrdersService';
import { Order } from '../../../types';

interface Props {
    order: Order;
    tone: StatusNoticeTone;
    onAddTracking: (order: Order) => void;
    onHandOver: (orderId: number) => void;
    onConfirmOrder: (orderId: number) => void;
    onReject: (orderId: number) => void;
    onEdit: (order: Order) => void;
    serviceFeeStatus: ServiceFeeBalanceStatus;
}

export const SalesStatusPanel: FC<Props> = ({
    order,
    tone,
    onAddTracking,
    onHandOver,
    onConfirmOrder,
    onReject,
    onEdit,
    serviceFeeStatus,
}) => {
    return (
        <>
            {order.seller_editable && !order.pending_revision && (
                <div className="mt-4">
                    <Button
                        type="button"
                        size="sm"
                        variant="outlinePrimary"
                        onClick={() => onEdit(order)}
                    >
                        <Pencil className="mr-1 h-4 w-4" />
                        Редагувати замовлення
                    </Button>
                </div>
            )}

            {order.status === 'awaiting_shipment' && (
                <OrderTelegramContactNotice
                    telegramUsername={order.user?.telegram_username}
                    message="Зв'яжіться з покупцем через Telegram:"
                    tone={tone}
                />
            )}

            {order.status === 'awaiting_shipment' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        order.delivery?.method === 'pickup'
                            ? 'Замовлення очікує особистої передачі покупцю'
                            : 'Замовлення очікує відправки'
                    }
                    action={
                        <div className="flex flex-wrap items-center gap-2">
                            {!order.pending_revision &&
                            order.delivery?.method === 'pickup' ? (
                                <Button
                                    size="sm"
                                    className="bg-green-600 text-white hover:bg-green-700"
                                    onClick={() => onHandOver(order.id)}
                                >
                                    <HandMetal className="mr-1 h-4 w-4" />
                                    Передано особисто
                                </Button>
                            ) : !order.pending_revision ? (
                                <Button
                                    size="sm"
                                    className="bg-blue-600 text-white hover:bg-blue-700"
                                    onClick={() => onAddTracking(order)}
                                >
                                    <Truck className="mr-1 h-4 w-4" />
                                    Додати ТТН
                                </Button>
                            ) : null}
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50 dark:bg-red-200/12 dark:text-red-300 dark:hover:bg-red-300/8"
                                onClick={() => onReject(order.id)}
                            >
                                Скасувати замовлення
                            </Button>
                        </div>
                    }
                />
            )}

            {(order.status === 'pending' || order.status === 'paid') && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        order.delivery?.method === 'pickup'
                            ? 'Нове замовлення! Підтвердіть і підготуйте до передачі'
                            : 'Нове замовлення! Підтвердіть і підготуйте до відправки'
                    }
                    action={
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                className="bg-green-600 text-white hover:bg-green-700"
                                onClick={() => onConfirmOrder(order.id)}
                                disabled={serviceFeeStatus.confirmationBlocked}
                            >
                                Підтвердити замовлення
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50 dark:bg-red-200/12 dark:text-red-300 dark:hover:bg-red-300/8"
                                onClick={() => onReject(order.id)}
                            >
                                Відхилити
                            </Button>
                        </div>
                    }
                />
            )}

            {(order.status === 'pending' || order.status === 'paid') &&
                serviceFeeStatus.confirmationBlocked && (
                    <p className="mt-2 text-sm text-destructive">
                        Підтвердження недоступне через неоплачені сервісні
                        збори. До сплати:{' '}
                        <strong>
                            {formatServiceFeeMoney(
                                serviceFeeStatus.pendingServiceFeesCents,
                            )}
                        </strong>
                        .
                    </p>
                )}

            {order.status === 'shipped' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Truck}
                    message={
                        OrdersService.waitingForRecipient(
                            order.status,
                            order.delivery,
                        )
                            ? 'Відправлення прибуло у точку видачі та очікує отримання покупцем.'
                            : 'Замовлення відправлено. Очікуємо оновлення статусу від перевізника.'
                    }
                    action={
                        order.delivery?.method !== 'pickup' &&
                        !OrdersService.waitingForRecipient(
                            order.status,
                            order.delivery,
                        ) ? (
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full border-blue-300 text-blue-700 hover:bg-blue-100 sm:w-auto"
                                onClick={() => onAddTracking(order)}
                            >
                                Змінити ТТН
                            </Button>
                        ) : undefined
                    }
                />
            )}

            {order.status === 'handed_over' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={HandMetal}
                    message="Передано покупцю. Очікуємо підтвердження отримання."
                />
            )}

            {order.status === 'delivered' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        order.delivery?.method === 'pickup'
                            ? 'Покупець підтвердив отримання. Замовлення буде завершено автоматично.'
                            : 'Перевізник підтвердив отримання. Замовлення буде завершено автоматично.'
                    }
                />
            )}

            {order.status === 'completed' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message="Замовлення виконано."
                />
            )}

            {order.status === 'cancelled' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        <>
                            Замовлення скасовано покупцем.
                            <CancellationDetails order={order} />
                        </>
                    }
                />
            )}

            {order.status === 'auto_cancelled' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Clock}
                    message="Замовлення автоматично скасовано."
                />
            )}

            {order.status === 'rejected' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        <>
                            Ви скасували замовлення.
                            <CancellationDetails order={order} />
                        </>
                    }
                />
            )}

            {order.status === 'returned' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Truck}
                    message="Перевізник повернув відправлення."
                />
            )}

            {order.status === 'dispute' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Clock}
                    message="За цим замовленням відкрито суперечку."
                />
            )}
        </>
    );
};
